import { memo, type ReactNode } from "react";
import { Box, Card, Skeleton, Tooltip, Typography } from "@mui/material";
import { alpha } from "@mui/material/styles";

type EcommerceQuickStatCardProps = {
  label: string;
  value: number;
  selected: boolean;
  disabled?: boolean;
  valueLoading?: boolean;
  color: string;
  icon: ReactNode;
  tooltip: string;
  onClick: () => void;
  /** Opacidade do fundo quando selecionado (0–1). */
  selectedAlpha?: number;
};

export const EcommerceQuickStatCard = memo(function EcommerceQuickStatCard({
  label,
  value,
  selected,
  disabled = false,
  valueLoading = false,
  color,
  icon,
  tooltip,
  onClick,
  selectedAlpha = 0.22,
}: EcommerceQuickStatCardProps) {
  return (
    <Tooltip title={tooltip} placement="top">
      <span style={{ display: "flex", flex: "1 1 148px", minWidth: 0 }}>
        <Card
          component="button"
          type="button"
          disabled={disabled}
          onClick={() => {
            if (disabled) return;
            onClick();
          }}
          sx={{
            p: 1,
            width: "100%",
            minHeight: 56,
            display: "flex",
            alignItems: "center",
            gap: 1,
            textAlign: "left",
            cursor: disabled ? "not-allowed" : "pointer",
            border: "1px solid",
            borderColor: selected ? color : `${color}30`,
            bgcolor: selected ? alpha(color, selectedAlpha) : "background.paper",
            boxShadow: selected ? 1 : 0,
            opacity: disabled ? 0.72 : 1,
            transition: "all 0.2s ease-in-out",
            "&:hover": disabled
              ? {}
              : {
                  borderColor: color,
                  boxShadow: 1,
                },
            "&.Mui-disabled": {
              opacity: 0.72,
              color: "inherit",
            },
          }}
        >
          <Box
            sx={{
              width: 32,
              height: 32,
              borderRadius: 1,
              display: "grid",
              placeItems: "center",
              flexShrink: 0,
              bgcolor: `${color}14`,
              color,
            }}
          >
            {icon}
          </Box>
          <Box sx={{ minWidth: 0 }}>
            {disabled || valueLoading ? (
              <Skeleton variant="text" width={28} height={24} />
            ) : (
              <Typography
                variant="h6"
                fontWeight={800}
                lineHeight={1.1}
                color={color}
                sx={{ fontVariantNumeric: "tabular-nums" }}
              >
                {value}
              </Typography>
            )}
            <Typography
              variant="caption"
              color="text.secondary"
              fontWeight={500}
              sx={{
                display: "-webkit-box",
                WebkitLineClamp: 2,
                WebkitBoxOrient: "vertical",
                overflow: "hidden",
                lineHeight: 1.25,
              }}
            >
              {label}
            </Typography>
          </Box>
        </Card>
      </span>
    </Tooltip>
  );
});
